import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Paper, Typography, Stack, TextField, MenuItem, ToggleButton, ToggleButtonGroup,
    Button, Chip, Alert, Skeleton, Divider, useMediaQuery, useTheme,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import RefreshIcon from '@mui/icons-material/Refresh';
import BarChartIcon from '@mui/icons-material/BarChart';
import axios from 'axios';
import { getSessionCookie } from '../../services/auth';
import { ATALHOS_PERIODO } from './PainelFiltros';

/**
 * Gráficos de atendimento da clínica.
 *
 * <h3>Por que uma requisição só</h3>
 * Os três gráficos saem de `GET /api/relatorios/graficos/atendimentos`, que
 * devolve as três séries já somadas pelo banco. Um endpoint por gráfico daria
 * três consultas do mesmo período e três chances de os totais discordarem entre
 * si; e contar no navegador exigiria baixar as sessões do período — histórico
 * clínico inteiro para exibir um número.
 *
 * <h3>Leitura sem depender de cor</h3>
 * Cada barra traz o valor escrito (`barLabel="value"`) e a categoria vem no
 * eixo, não na legenda: quem não distingue as cores lê o gráfico pelo texto. As
 * cores repetem as que o painel do dia já usa para cada situação.
 *
 * <h3>Por que esta chamada nao usa o `api`</h3>
 * O interceptor global desloga em qualquer 401 (services/api.js). Como esta
 * secao vive dentro do painel "Hoje", um 401 vindo so daqui derrubaria a
 * sessao na primeira tela do sistema. A requisicao dos graficos vai por um
 * axios sem interceptor, com o mesmo token: falha dela vira o aviso de erro
 * do cartao, e quem decide sobre a sessao continuam sendo as outras chamadas
 * da pagina.
 *
 * <h3>Permissão</h3>
 * O escopo é decidido no back-end. Quando ele responde `escopoRestrito`, o
 * usuário é um psicólogo vendo os próprios atendimentos: o seletor de
 * profissional sai da tela em vez de oferecer um filtro que seria ignorado.
 */

/** Cores por situação — as mesmas do painel do dia. */
const COR_SITUACAO = {
    REALIZADOS: '#2e7d32',
    AGENDADOS: '#0277bd',
    CANCELADOS: '#c62828',
    FALTAS: '#ef6c00',
};

const COR_MARCA = '#1ABB9C';

const GRANULARIDADES = [
    { valor: 'DIA', rotulo: 'Dia' },
    { valor: 'SEMANA', rotulo: 'Semana' },
    { valor: 'MES', rotulo: 'Mês' },
];

/**
 * "Últimos 30 dias" é o atalho que PainelFiltros já define (29 dias atrás +
 * hoje). Se alguém renomear o atalho lá, o último da lista ainda serve — a tela
 * não pode abrir sem período.
 */
const ATALHO_PADRAO = ATALHOS_PERIODO.find(a => a.rotulo === 'Últimos 30 dias')
    ?? ATALHOS_PERIODO[ATALHOS_PERIODO.length - 1];
const PERIODO_INICIAL = ATALHO_PADRAO.calcular();

const dataBr = (iso) => {
    if (!iso) return '';
    const [a, m, d] = iso.split('-');
    return `${d}/${m}/${a}`;
};

const dataCompleta = (iso) => (iso && iso.length === 10 ? iso : null);

/**
 * @param embutido  true quando a secao vive dentro de outra pagina (o painel
 *                  "Hoje"): tira o padding e o titulo de pagina, que ali seriam
 *                  uma segunda moldura dentro da moldura do dashboard.
 */
export default function GraficosAtendimentos({ embutido = false }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [dataInicio, setDataInicio] = useState(PERIODO_INICIAL.dataInicio);
    const [dataFim, setDataFim] = useState(PERIODO_INICIAL.dataFim);
    const [granularidade, setGranularidade] = useState('DIA');
    const [idPsicologo, setIdPsicologo] = useState('');

    const [dados, setDados] = useState(null);
    const [psicologos, setPsicologos] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');

    const carregar = useCallback(async () => {
        // Data incompleta no campo nativo (o usuário ainda está digitando o ano)
        // não vira requisição — senão cada tecla é uma consulta.
        if (!dataCompleta(dataInicio) || !dataCompleta(dataFim)) return;

        setCarregando(true);
        setErro('');
        try {
            const token = getSessionCookie();
            const res = await axios.get('/api/relatorios/graficos/atendimentos', {
                params: {
                    dataInicio,
                    dataFim,
                    granularidade,
                    idPsicologo: idPsicologo || undefined,
                },
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            setDados(res.data);
        } catch (e) {
            setDados(null);
            const status = e?.response?.status;
            setErro(
                status === 401 ? 'Sessão expirada para esta consulta. Recarregue a página.'
                    : status === 403 ? 'Você não tem permissão para ver estes gráficos.'
                        : 'Não foi possível carregar os gráficos.');
        } finally {
            setCarregando(false);
        }
    }, [dataInicio, dataFim, granularidade, idPsicologo]);

    useEffect(() => { carregar(); }, [carregar]);

    // Lista de profissionais só para o seletor. Uma vez por abertura da tela.
    useEffect(() => {
        let ativo = true;
        const token = getSessionCookie();
        axios.get('/api/psicologos', { headers: token ? { Authorization: `Bearer ${token}` } : {} })
            .then(res => {
                const lista = Array.isArray(res.data) ? res.data : (res.data?.content ?? []);
                if (ativo) setPsicologos(lista);
            })
            .catch(() => { if (ativo) setPsicologos([]); });
        return () => { ativo = false; };
    }, []);

    const escopoRestrito = !!dados?.escopoRestrito;
    const vazio = !!dados && dados.total === 0;

    const serie = (pontos) => ({
        rotulos: (pontos ?? []).map(p => p.rotulo),
        valores: (pontos ?? []).map(p => p.quantidade),
    });

    const periodo = useMemo(() => serie(dados?.porPeriodo), [dados]);
    const situacao = useMemo(() => serie(dados?.porSituacao), [dados]);
    const porPsicologo = useMemo(() => serie(dados?.porPsicologo), [dados]);

    const coresSituacao = (dados?.porSituacao ?? []).map(p => COR_SITUACAO[p.chave] ?? COR_MARCA);

    const aplicarAtalho = (atalho) => {
        const { dataInicio: i, dataFim: f } = atalho.calcular();
        setDataInicio(i);
        setDataFim(f);
    };

    return (
        <Box sx={{ p: embutido ? 0 : { xs: 2, md: 3 } }}>
            <Typography
                variant={embutido ? 'h6' : 'h5'}
                sx={{ fontWeight: 600, mb: 0.5, color: 'secondary.main' }}
            >
                Gráficos de atendimento
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Quantidade de atendimentos por período, situação e profissional.
            </Typography>

            {/* ── Filtros ───────────────────────────────────────────────── */}
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    sx={{ alignItems: { xs: 'stretch', md: 'center' }, flexWrap: 'wrap' }}
                >
                    <TextField
                        size="small"
                        type="date"
                        label="De"
                        value={dataInicio}
                        onChange={(e) => setDataInicio(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 150 }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="Até"
                        value={dataFim}
                        onChange={(e) => setDataFim(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 150 }}
                    />

                    {!escopoRestrito && (
                        <TextField
                            select
                            size="small"
                            label="Psicólogo"
                            value={idPsicologo}
                            onChange={(e) => setIdPsicologo(e.target.value)}
                            sx={{ minWidth: 200 }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {psicologos.map(p => (
                                <MenuItem key={p.id_psicologo} value={p.id_psicologo}>
                                    {p.nomeCompleto}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}

                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={granularidade}
                        onChange={(_e, v) => { if (v) setGranularidade(v); }}
                        aria-label="Agrupar o gráfico de período por"
                    >
                        {GRANULARIDADES.map(g => (
                            <ToggleButton key={g.valor} value={g.valor} sx={{ px: 1.5 }}>
                                {g.rotulo}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {ATALHOS_PERIODO.map(a => (
                            <Chip
                                key={a.rotulo}
                                label={a.rotulo}
                                size="small"
                                variant="outlined"
                                onClick={() => aplicarAtalho(a)}
                            />
                        ))}
                    </Box>

                    <Button
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={carregar}
                        sx={{ ml: { md: 'auto' } }}
                    >
                        Atualizar
                    </Button>
                </Stack>
            </Paper>

            {/* ── Intervalo exibido ─────────────────────────────────────── */}
            {dados && (
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}
                    role="status"
                    aria-live="polite"
                >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {`Exibindo ${dataBr(dados.dataInicio)} a ${dataBr(dados.dataFim)}`}
                    </Typography>
                    <Chip
                        size="small"
                        label={`${dados.total} ${dados.total === 1 ? 'atendimento' : 'atendimentos'}`}
                        sx={{ fontWeight: 600 }}
                    />
                    {escopoRestrito && (
                        <Chip size="small" color="info" variant="outlined" label="Somente os seus atendimentos" />
                    )}
                </Stack>
            )}

            {erro && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={<Button color="inherit" size="small" onClick={carregar}>Tentar de novo</Button>}
                >
                    {erro}
                </Alert>
            )}

            {carregando && (
                <Stack spacing={2}>
                    <Skeleton variant="rounded" height={320} />
                    <Skeleton variant="rounded" height={280} />
                    <Skeleton variant="rounded" height={280} />
                </Stack>
            )}

            {!carregando && !erro && vazio && (
                <Paper variant="outlined" sx={{ p: 5, textAlign: 'center' }} role="status">
                    <BarChartIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Nenhum atendimento no período
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {`Não há sessões registradas entre ${dataBr(dados.dataInicio)} e ${dataBr(dados.dataFim)}`}
                        {dados.idPsicologo ? ' para o profissional selecionado.' : '.'}
                    </Typography>
                </Paper>
            )}

            {!carregando && !erro && dados && !vazio && (
                <Stack spacing={2}>
                    <Cartao
                        titulo="Atendimentos por período"
                        descricao={`Agrupado por ${GRANULARIDADES.find(g => g.valor === dados.granularidade)?.rotulo.toLowerCase()}`}
                    >
                        <BarChart
                            height={isMobile ? 260 : 320}
                            xAxis={[{
                                data: periodo.rotulos,
                                scaleType: 'band',
                                tickLabelStyle: { fontSize: 11, angle: periodo.rotulos.length > 12 ? -45 : 0, textAnchor: periodo.rotulos.length > 12 ? 'end' : 'middle' },
                            }]}
                            yAxis={[{ label: 'Atendimentos', min: 0 }]}
                            series={[{ data: periodo.valores, label: 'Atendimentos', color: COR_MARCA }]}
                            barLabel={periodo.rotulos.length <= 12 ? 'value' : undefined}
                            hideLegend
                            margin={{ left: 8, right: 8, top: 16, bottom: 8 }}
                        />
                    </Cartao>

                    <Cartao
                        titulo="Situação dos atendimentos"
                        descricao="Realizados inclui sessões alteradas; agendados inclui confirmadas e reagendadas"
                    >
                        <BarChart
                            height={isMobile ? 240 : 300}
                            xAxis={[{
                                data: situacao.rotulos,
                                scaleType: 'band',
                                colorMap: { type: 'ordinal', colors: coresSituacao },
                            }]}
                            yAxis={[{ label: 'Atendimentos', min: 0 }]}
                            series={[{ data: situacao.valores, label: 'Atendimentos' }]}
                            barLabel="value"
                            hideLegend
                            margin={{ left: 8, right: 8, top: 16, bottom: 8 }}
                        />
                    </Cartao>

                    <Cartao
                        titulo="Atendimentos por psicólogo"
                        descricao={escopoRestrito ? 'Apenas o seu vínculo' : 'Do maior para o menor volume'}
                    >
                        <BarChart
                            layout="horizontal"
                            height={Math.max(isMobile ? 200 : 240, porPsicologo.rotulos.length * 44)}
                            yAxis={[{
                                data: porPsicologo.rotulos,
                                scaleType: 'band',
                                width: isMobile ? 110 : 180,
                                tickLabelStyle: { fontSize: 11 },
                            }]}
                            xAxis={[{ label: 'Atendimentos', min: 0 }]}
                            series={[{ data: porPsicologo.valores, label: 'Atendimentos', color: COR_MARCA }]}
                            barLabel="value"
                            hideLegend
                            margin={{ left: 8, right: 16, top: 16, bottom: 8 }}
                        />
                    </Cartao>
                </Stack>
            )}
        </Box>
    );
}

/** Moldura dos gráficos: mesmo Paper discreto das outras telas de relatório. */
function Cartao({ titulo, descricao, children }) {
    return (
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{titulo}</Typography>
            {descricao && (
                <Typography variant="caption" color="text.secondary">{descricao}</Typography>
            )}
            <Divider sx={{ my: 1 }} />
            {/* O gráfico mede a largura do pai; overflow fica no container, não na página. */}
            <Box sx={{ width: '100%', overflowX: 'hidden' }}>{children}</Box>
        </Paper>
    );
}
